// Small shared presentational primitives for the District Insights dashboard.
import React from "react";
import {
  STATUS,
  TONES,
  INK,
  MUTED,
  CONTEXT,
  SURFACE,
  MASTERY_BANDS,
  OUT_OF_SESSION_COLOR,
  UTIL_STATES,
  QUADRANT_TONE,
} from "./tokens";
import { clamp } from "./model";
import { rowTone } from "./status";
import { MAX_BAR_THICKNESS } from "./charts";

// Faint fill marking the buckets inside a detected focus cycle. Shared by the
// Trend sparkline and the pattern micro-bar so one row's two marks agree about
// where the push was, and it matches the tint on the Usage lens's mix strip.
//
// Deliberately just a band: no icon, no per-row prose. A cycle is context for
// reading the line, not a finding about the row.
const CYCLE_BAND_FILL = "rgba(176, 116, 26, 0.16)";

// The outline that goes with that fill, where a band is big enough to carry one:
// the adoption ribbon's per-school cycles and the mix strip's district bands. Fill
// alone reads as a background; fill plus this stroke reads as an annotation, and
// the two plots on the Usage lens have to mark a cycle the same way or a reader
// has to learn the notation twice.
const CYCLE_BAND_STROKE = "#b0741a";

// `bands` is a list of inclusive [lo, hi] bucket-index pairs (a window can hold a
// fall push and a spring one). Rendered as SVG rects behind the marks.
const CycleBands = ({ bands, count, width, height }) => {
  if (!bands || !bands.length || !count) return null;
  const step = width / Math.max(count - 1, 1);
  return (
    <>
      {bands.map(([lo, hi], i) => {
        // Half a step of padding either side so the band covers the buckets
        // rather than stopping at their centre points.
        const x = Math.max(0, (lo - 0.5) * step);
        const w = Math.min(width - x, (hi - lo + 1) * step);
        if (w <= 0) return null;
        return (
          <rect
            key={i}
            x={x.toFixed(1)}
            y={0}
            width={w.toFixed(1)}
            height={height}
            fill={CYCLE_BAND_FILL}
          />
        );
      })}
    </>
  );
};

// tiny inline SVG sparkline of a numeric series
const Sparkline = ({
  values,
  color = INK,
  width = 96,
  height = 24,
  bands,
  title,
}) => {
  if (!values || values.length < 2)
    return <span style={{ color: MUTED, fontSize: 11 }}>—</span>;
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const span = max - min || 1;
  const step = width / (values.length - 1);
  const coords = values.map((v, i) => {
    const x = i * step;
    const y = height - ((v - min) / span) * (height - 3) - 1.5;
    return [x, y];
  });
  const poly = coords.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const [lx, ly] = coords[coords.length - 1];
  return (
    <svg width={width} height={height} style={{ display: "block" }}>
      {title && <title>{title}</title>}
      {/* behind the line, so the mark stays the thing you read */}
      <CycleBands
        bands={bands}
        count={values.length}
        width={width}
        height={height}
      />
      <polyline
        points={poly}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <circle cx={lx.toFixed(1)} cy={ly.toFixed(1)} r="2.2" fill={color} />
    </svg>
  );
};

// The pattern micro-bar: one row's lesson volume over the window, as colour
// density rather than as a line.
//
// Density, not bar heights, for two reasons. It sits immediately beside the Trend
// sparkline, and two line-shaped marks in adjacent cells invite reading one as the
// other — this is a different quantity (lessons, not mastery per learner). And it
// is the same visual language as the district adoption ribbon, so "whatever the
// ribbon shows for the district, this shows for one row" holds literally: same
// encoding, one row of it.
//
// Scaled to the row's OWN peak, so it answers "when did this unit work" and not
// "how does its volume compare to the district's" — the latter is what roster-sized
// bubbles and the reach axis are for, and a district-scaled bar would render every
// small school as a blank strip.
const PATTERN_MIN_ALPHA = 0.12;
const PatternBar = ({
  values,
  color = INK,
  width = 72,
  height = 10,
  bands,
  title,
}) => {
  if (!values || !values.length)
    return <span style={{ color: MUTED, fontSize: 11 }}>—</span>;
  const max = Math.max(...values);
  const cellW = width / values.length;
  return (
    <svg width={width} height={height} style={{ display: "block" }}>
      {title && <title>{title}</title>}
      <CycleBands
        bands={bands}
        count={values.length}
        width={width}
        height={height}
      />
      {values.map((v, i) => {
        if (!v || !max) return null;
        return (
          <rect
            key={i}
            x={(i * cellW).toFixed(2)}
            y={0}
            // -0.5 leaves a hairline between cells so a long run still reads as
            // buckets rather than as one solid block
            width={Math.max(cellW - 0.5, 0.5).toFixed(2)}
            height={height}
            fill={color}
            opacity={(PATTERN_MIN_ALPHA + (1 - PATTERN_MIN_ALPHA) * (v / max)).toFixed(3)}
          />
        );
      })}
    </svg>
  );
};

// small last-period delta shown under a table number. Green up / red down; the
// direction reads as "improving / slipping" regardless of the metric's sign.
//
// The period ("last month") is a tooltip, not inline text: spelled out in every
// cell it wrapped to three lines in the narrow worklist columns, tripling row
// height and pushing the Status column off the right edge. Each table states its
// bucket once in the prose above instead.
const DeltaChip = ({ value, digits = 1, unit = "", period = "period" }) => {
  if (value == null) return null;
  const up = value > 0.05 / 10 ** digits;
  const down = value < -0.05 / 10 ** digits;
  const color = up ? STATUS.good.color : down ? STATUS.critical.color : MUTED;
  return (
    <div
      title={`vs. last ${period}`}
      style={{
        fontSize: 11,
        fontWeight: 600,
        color,
        marginTop: 2,
        whiteSpace: "nowrap",
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {up ? "▲" : down ? "▼" : "="} {Math.abs(value).toFixed(digits)}
      {unit}
    </div>
  );
};

// ---- verdict chips ---------------------------------------------------------

// The tone chip: a dot, a word, and a tinted surface. Used wherever the
// dashboard states a VERDICT rather than plots a value — the Overview's digest
// cards today, and anything that grows a "how bad is this" line later.
//
// Tinted, not bare text, because these chips have to be legible as a set: three
// of them sit in a row on the landing page and the reader's question is "which
// one is the red one". A colored word alone answers that too slowly at 11px, and
// amber can't be the word (2.2:1 on white — see TONES' note on `ink`).
const ToneChip = ({ tone, label, title, icon }) => {
  const t = TONES[tone] || TONES.clear;
  return (
    <span
      title={title}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 0.2,
        color: t.ink,
        background: t.tint,
        border: `1px solid ${t.tint}`,
        borderRadius: 999,
        padding: "3px 9px 3px 7px",
        whiteSpace: "nowrap",
      }}
    >
      {icon || (
        <span
          style={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            background: t.color,
            display: "inline-block",
            flex: "0 0 auto",
          }}
        />
      )}
      {label || t.label}
    </span>
  );
};

// One pill, three callers.
//
// The cohort drill-down panels pass `status` alone and get the quadrant label,
// toned via QUADRANT_TONE. The Adoption worklist passes a `tone` plus its own
// `label`, because its statuses name a KIND of situation rather than a quadrant —
// seven words over four tones, so the worklist gains no new visual axis. `title`
// carries the one-line explanation; "Shallow" and "Bursty" are not self-evident
// and the legend can't be everywhere.
//
// Untinted (a dot and a word, not a chip) on purpose: these sit in a table
// column, dozens at a time, where a tinted pill per row would out-shout the
// numbers beside it. The chip treatment is for the handful of card-level
// verdicts.
const StatusPill = ({ status, tone, label, title }) => {
  const key = tone || QUADRANT_TONE[status] || "attention";
  const t = TONES[key] || TONES.attention;
  const text = label || (STATUS[status] && STATUS[status].label) || key;
  return (
    <span
      title={title}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        fontWeight: 600,
        color: t.ink,
      }}
    >
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: t.color,
          display: "inline-block",
          flex: "0 0 auto",
        }}
      />
      {text}
    </span>
  );
};

// ---- digest sparks -----------------------------------------------------------
// Card-sized previews of the two full plots the digest cards link to. These are
// SPARKS, not charts: no axes, no ticks, no hover. The card's own number and
// sentence carry the precision, and the destination page carries the real figure
// with its tooltips — so nothing here is the only route to a value. Their job is
// to show the SHAPE of the finding and to make the click-through recognizable
// (you land on a bigger version of what you just clicked).
//
// Inline SVG / divs rather than chart.js, for the same reason the curriculum
// table's composition bars are plain divs: at this size a canvas, its options
// object, and a second chart instance per card buy nothing.

// Mini reach x depth quadrant — the Schools page's scatter at card size.
//
// The median crosshair is the one piece of chrome that MUST survive the shrink:
// without it, a scatter stripped of axes has no reference and "far out" is
// unreadable. Everything else the full plot draws (axis titles, quadrant
// captions, comet trails, roster-scaled bubbles, the distance-graded alpha ramp)
// is dropped — at this size none of it resolves.
//
// Two things that work at 440px and fail at 68px, both learned by looking at it:
//
//  1. RAW axes collapse. The full plot scales y to 1.15x the deepest school; one
//     small building with an extreme standards-per-learner sets that bound and
//     everything else lands in a flat line on the floor. So this plots the
//     STANDARDIZED coordinates buildQuad already computes (z from the district
//     median, robust-scaled) and clamps to +/-LIM: the cloud sits around the
//     crosshair, the far-out schools sit at the edges, and one extreme school
//     can't rescale everyone. Distance-from-normal is also the quantity the
//     card's count is computed from, so the picture matches the number.
//  2. Divs, not SVG. A viewBox with preserveAspectRatio="none" stretches to the
//     card width, which turns every <circle> into an ellipse. Absolutely
//     positioned round divs are fixed-size in px and positioned in %, so they
//     stay circular at any card width.
// Sigma at the edge of the box, fitted to the data between these bounds. A fixed
// 3σ box wasted its outer half on most districts and squeezed the cloud into the
// middle third; the floor keeps the 1.5σ outlier ring comfortably inside the box
// even when nothing is far out, and the ceiling stops one extreme school from
// compressing everyone else back into the center.
const QUAD_LIM_MIN = 2;
const QUAD_LIM_MAX = 3.5;
const QuadSpark = ({ points, outlierMode, height = 120 }) => {
  if (!points || !points.length) return null;
  // ONE limit for both axes, deliberately: the card's count comes from Euclidean
  // distance in this space, so scaling the axes independently to fill the box
  // would make equal on-screen distances mean unequal sigma and the picture would
  // stop matching the number.
  const maxAbs = points.reduce(
    (m, p) => Math.max(m, Math.abs(p.zReach || 0), Math.abs(p.zDepth || 0)),
    0
  );
  const lim = clamp(maxAbs, QUAD_LIM_MIN, QUAD_LIM_MAX);
  // inset so a dot at the clamp limit keeps its full diameter inside the box
  const fx = (z) => 50 + (clamp(z, -lim, lim) / lim) * 46;
  const fy = (z) => 50 - (clamp(z, -lim, lim) / lim) * 42;
  // typical dots first so outliers paint on top of the cloud, never under it
  const ordered = [...points].sort(
    (a, b) => (a.isOutlier ? 1 : 0) - (b.isOutlier ? 1 : 0)
  );
  const rule = { position: "absolute", borderColor: "#c3c2b7" };
  // The box is WIDE, not square, so one sigma limit across both axes puts them on
  // a common scale in data terms but not in pixels — a school 2σ out on reach
  // travels further across the card than one 2σ out on mastery. That's tolerable
  // here for two reasons: which schools are far out is carried by color, not by
  // the reader measuring distance, and the full Schools scatter this previews is
  // itself wide with independently scaled axes — a square spark would look LESS
  // like the plot you land on. What survives the shrink is the shape of the
  // spread and which corner the flagged schools sit in.
  return (
    <div style={{ position: "relative", height, width: "100%" }}>
      <div style={{ ...rule, left: "50%", top: 0, bottom: 0, borderLeft: "1px dashed #c3c2b7" }} />
      <div style={{ ...rule, top: "50%", left: 0, right: 0, borderTop: "1px dashed #c3c2b7" }} />
      {ordered.map((p, i) => {
        // Emphasis, not categorical: outliers take their TONE color -- the same
        // one their pill and their trend line wear on the page this links to --
        // and the rest recede to context gray. In small-district mode there is no
        // "normal" to deviate from, so every dot is colored by its own standing
        // instead.
        const emph = !outlierMode || p.isOutlier;
        const d = emph ? 8 : 6;
        return (
          <div
            key={`${p.name}-${i}`}
            title={p.name}
            style={{
              position: "absolute",
              left: `${fx(p.zReach)}%`,
              top: `${fy(p.zDepth)}%`,
              width: d,
              height: d,
              marginLeft: -d / 2,
              marginTop: -d / 2,
              borderRadius: "50%",
              background: emph ? (TONES[rowTone(p)] || TONES.attention).color : CONTEXT,
              opacity: emph ? 0.9 : 0.6,
              // surface ring so overlapping dots stay countable
              boxShadow: `0 0 0 1px ${SURFACE}`,
            }}
          />
        );
      })}
    </div>
  );
};

// One part-to-whole mastery bar — the curriculum table's composition row at card
// size. Segments are flex-grown by their counts so the 2px surface gaps between
// them come out of the track rather than overflowing it, and empty segments are
// dropped so a zero never leaves a stray gap.
//
// A Spark like its neighbours, and named like them: no labels, no tooltips, no
// axis. The curriculum table's full bar carries the same name in its own file,
// and the two are not the same component -- that one has share/count modes,
// in-bar figures and per-segment rounding, none of which resolve at 9px.
const CompositionSpark = ({ row, height = 9 }) => {
  const segs = MASTERY_BANDS.filter((b) => (row[b.key] || 0) > 0);
  return (
    <div
      style={{
        display: "flex",
        gap: 2,
        height,
        borderRadius: height / 2,
        overflow: "hidden",
        background: "#efeee8",
      }}
    >
      {segs.map((b) => (
        <div key={b.key} style={{ flex: `${row[b.key]} 0 0`, background: b.color }} />
      ))}
    </div>
  );
};

// Mini per-bucket fleet state strip — the Fleet tab's stacked bars at card size.
// Bar height is the deployed fleet (so the year's rollout is visible as growth),
// active anchored to the baseline, out-of-session buckets shaded behind.
//
// Divs rather than a second chart.js instance: the full version's whole value-add
// over this is its axis, tooltips, and the oos-shading plugin, none of which
// survive at 76px. What must survive is the shading — without it a summer of gray
// bars reads as a fleet that died rather than a school that closed.
const FLEET_SPARK_ORDER = ["dark", "connected", "active"]; // active last = baseline
// Headroom above the tallest bar. Without it the shade is invisible on any fleet
// that doesn't grow during the year: the peak bucket's bar is the full column
// height, so it covers the very band that's supposed to mark the month closed —
// and on a flat fleet EVERY bucket is at or near the peak. The extra 12% costs a
// few pixels of bar and buys a visible shaded band over each closed month.
const FLEET_SPARK_HEADROOM = 1.12;
const FleetSpark = ({ buckets, height = 120 }) => {
  if (!buckets || !buckets.length) return null;
  const total = (b) => b.active + b.connected + b.dark;
  const max = Math.max(...buckets.map(total), 1) * FLEET_SPARK_HEADROOM;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-end",
        gap: 2,
        height,
        width: "100%",
      }}
    >
      {buckets.map((b, i) => (
        <div
          key={i}
          title={`${b.label} — ${b.active} active, ${b.connected} connected, ` +
            `${b.dark} dark${b.oos ? " (out of session)" : ""}` +
            (b.partial ? " (in progress — a partial period)" : "")}
          style={{
            flex: 1,
            minWidth: 0,
            // Cap the column at the Fleet tab's own bar cap -- the shared
            // constant, so the two never drift apart. A school year that has just
            // started has ONE bucket, and uncapped that single column fills the
            // card — a solid gray block that reads as a broken panel rather than
            // as a one-month strip. At a full 12 buckets the columns land well
            // under this, so a complete year is unaffected.
            maxWidth: MAX_BAR_THICKNESS,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-end",
            // the shade fills the full column height, not the bar's, so the
            // closed months read as a background band across the strip
            background: b.oos ? OUT_OF_SESSION_COLOR : "transparent",
            borderRadius: 2,
          }}
        >
          <div
            style={{
              height: `${(100 * total(b)) / max}%`,
              display: "flex",
              flexDirection: "column",
              gap: 1, // 2px visual surface gap between stacked segments
              borderRadius: 2,
              overflow: "hidden",
              // A bucket the window cut short is drawn faded, because its states
              // cover days where its neighbours cover a month: a month one day
              // old is almost entirely dark, and at full strength that column
              // reads as a fleet that just died rather than one that has barely
              // been asked yet. Fading rather than dropping it, because the
              // column is also the only place today's activity appears.
              opacity: b.partial ? 0.45 : 1,
            }}
          >
            {FLEET_SPARK_ORDER.filter((k) => b[k] > 0).map((k) => (
              <div
                key={k}
                style={{ flex: `${b[k]} 0 0`, background: UTIL_STATES[k].color }}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// Compact legend for the sparks above — 3+ segments means identity can never be
// color-alone, and these are too small for direct labels.
const SparkLegend = ({ items, note }) => (
  <div
    style={{
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      gap: 10,
      fontSize: 10,
      color: MUTED,
    }}
  >
    {items.map((it) => (
      <span
        key={it.label}
        style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
      >
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: it.round ? "50%" : 2,
            background: it.color,
            display: "inline-block",
          }}
        />
        {it.label}
      </span>
    ))}
    {note && <span>· {note}</span>}
  </div>
);

export {
  Sparkline,
  PatternBar,
  CycleBands,
  CYCLE_BAND_FILL,
  CYCLE_BAND_STROKE,
  DeltaChip,
  StatusPill,
  ToneChip,
  QuadSpark,
  CompositionSpark,
  FleetSpark,
  SparkLegend,
};
